import { runtimeDiagnostics } from "./runtime-diagnostics";

const RTC_SFU_RECOVERY_DEBUG_PARAM = "rtc_sfu_recovery_debug";
const RTC_SFU_RECOVERY_VERBOSE_PARAM = "rtc_sfu_recovery_verbose";
const RTC_SFU_PROTOO_REQUEST_TIMEOUT_PARAM = "rtc_sfu_protoo_request_timeout_ms";
const RTC_SFU_RECOVERY_LOG_PREFIX = "[rtc-sfu:recovery]";
const DEFAULT_RTC_SFU_PROTOO_REQUEST_TIMEOUT_MS = 10000;
const DEFAULT_RECOVERY_LOG_EVENTS = new Set([
  "auth-refresh:request:error",
  "chat:dispatch-public-error",
  "dialog-connect:closed-before-open",
  "dialog-connect:joined",
  "dialog-connect:join-error",
  "ice-restart:done",
  "ice-restart:recv-error",
  "ice-restart:send-error",
  "ice-restart:start",
  "join-room:joined",
  "local-media:producer-error",
  "protoo:close",
  "protoo:disconnected",
  "protoo:failed",
  "protoo:open",
  "protoo-request:retry-after-reconnect",
  "protoo-request:timeout",
  "protoo-request:timeout-recovery-start",
  "protoo-request:wait-for-ready",
  "recovery:connected",
  "recovery:done",
  "recovery:error",
  "recovery:start",
  "recovery:state",
  "ui:connection-state"
]);
const DEFAULT_TRANSPORT_LOG_STATES = new Set(["connected", "disconnected", "failed"]);

function truthyDebugValue(value) {
  return ["1", "true", "on", "yes"].includes(`${value || ""}`.toLowerCase());
}

function falseyDebugValue(value) {
  return ["0", "false", "off", "no"].includes(`${value || ""}`.toLowerCase());
}

function readQueryParam(name) {
  const search = typeof location !== "undefined" ? location.search : "";
  return new URLSearchParams(search).get(name);
}

function readLocalStorageValue(name) {
  return typeof window !== "undefined" ? window.localStorage?.getItem(name) : null;
}

function readBooleanFlag(name) {
  const queryValue = readQueryParam(name);

  if (truthyDebugValue(queryValue)) {
    return true;
  }

  if (falseyDebugValue(queryValue)) {
    return false;
  }

  return truthyDebugValue(readLocalStorageValue(name));
}

function readNonNegativeIntegerFlag(name, fallback) {
  const value = readQueryParam(name) ?? readLocalStorageValue(name);
  const parsedValue = Number.parseInt(value, 10);

  return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : fallback;
}

export function isRtcSfuRecoveryVerboseDebugEnabled() {
  try {
    return (
      readQueryParam(RTC_SFU_RECOVERY_DEBUG_PARAM) === "verbose" || readBooleanFlag(RTC_SFU_RECOVERY_VERBOSE_PARAM)
    );
  } catch (_) {
    return false;
  }
}

export function isRtcSfuRecoveryDebugEnabled() {
  try {
    return readBooleanFlag(RTC_SFU_RECOVERY_DEBUG_PARAM) || isRtcSfuRecoveryVerboseDebugEnabled();
  } catch (_) {
    return false;
  }
}

export function getRtcSfuProtooRequestTimeoutMs() {
  try {
    return readNonNegativeIntegerFlag(RTC_SFU_PROTOO_REQUEST_TIMEOUT_PARAM, DEFAULT_RTC_SFU_PROTOO_REQUEST_TIMEOUT_MS);
  } catch (_) {
    return DEFAULT_RTC_SFU_PROTOO_REQUEST_TIMEOUT_MS;
  }
}

export function summarizeRtcSfuEndpoint(endpoint = {}) {
  if (!endpoint) {
    return null;
  }

  return {
    serverUrl: endpoint.serverUrl,
    host: endpoint.host,
    port: endpoint.port,
    allocationId: endpoint.allocation?.allocationId,
    usingOverride: endpoint.usingOverride,
    usingRtcSfuOrchestrator: Boolean(endpoint.usingRtcSfuOrchestrator || endpoint.allocation),
    hasIceServers: Boolean((endpoint.iceServers || endpoint.ice_servers)?.length),
    turnEnabled: Boolean(endpoint.turn?.enabled)
  };
}

export function summarizeRtcSfuRoomSession(session = {}) {
  if (!session) {
    return null;
  }

  return {
    roomId: session.roomId,
    hubId: session.hubId,
    state: session.state,
    canEnter: session.canEnter,
    peerMode: session.peerMode,
    roomSessionId: session.roomSessionId,
    hasAllocationGrant: Boolean(session.allocationGrant),
    hasReconnectToken: Boolean(session.reconnectToken),
    mediaQualityProfile: session.roomMediaQuality?.profile
  };
}

export function summarizeRtcSfuError(error = {}) {
  if (!error) {
    return null;
  }

  return {
    name: error.name,
    message: error.message || error.reason || error.data?.message,
    status: error.status,
    statusText: error.statusText,
    code: error.code,
    url: error.url
  };
}

export function summarizeRtcSfuTrack(track = {}) {
  if (!track) {
    return null;
  }

  return {
    id: track.id,
    kind: track.kind,
    readyState: track.readyState,
    enabled: track.enabled,
    contentHint: track._hubs_contentHint
  };
}

function shouldLogDefaultRecoveryEvent(event, details = {}) {
  if (DEFAULT_RECOVERY_LOG_EVENTS.has(event)) {
    return true;
  }

  if (
    (event === "transport:send-state" || event === "transport:recv-state") &&
    DEFAULT_TRANSPORT_LOG_STATES.has(details.state)
  ) {
    return true;
  }

  return false;
}

export function logRtcSfuRecovery(event, details, requestContext) {
  try {
    runtimeDiagnostics.record(event, details, requestContext);
  } catch (_) {
    /* Best effort. */
  }

  if (!isRtcSfuRecoveryDebugEnabled()) {
    return;
  }

  if (!isRtcSfuRecoveryVerboseDebugEnabled() && !shouldLogDefaultRecoveryEvent(event, details)) {
    return;
  }

  if (details === undefined) {
    console.info(RTC_SFU_RECOVERY_LOG_PREFIX, event);
  } else {
    console.info(RTC_SFU_RECOVERY_LOG_PREFIX, event, details);
  }
}
